/**
 * Report Table Component
 * Responsive table for displaying RSVP report data
 * Supports summary, single-day, and cooking-day views
 */

import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate, fromISODate } from '@/lib/utils/dateHelpers';
import { cn } from '@/lib/utils';
import { LocationColorBadge } from '@/components/ui/location-color-badge';
import { getLocationBadgeStyles } from '@/lib/utils/locationColor';
import {
  compareLocationSorted,
  createFirstOfLocationSet,
  groupByLocation,
  type LocationSortableItem,
} from '@/lib/utils/locationSorting';
import { formatOptOutSummary, OPT_OUT_ALL_INCLUDED } from '@/lib/utils/optOutHelpers';
import {
  groupLocationSizeBreakdown,
  type LocationSizeBreakdownGroup,
} from '@/lib/utils/reportBreakdown';
import { RSVP_SIZES, type RSVPSizeCode } from '@/types/units';
import type {
  CookingDayMenuItem,
  CookingDayReportData,
  CookingDayReportRow,
  RSVPLocationBreakdownItem,
  RSVPReportItem,
  RSVPSummaryItem,
} from '@/types/api';

export type ReportView = 'summary' | 'signupSheet' | 'cookingDay';

interface ReportTableProps {
  /** Current view mode */
  view: ReportView;
  /** Summary data (for summary view) */
  summaryData: RSVPSummaryItem[];
  /** Detailed data (for sign up sheet view) */
  detailsData: RSVPReportItem[];
  /** Combined data for cooking day mode */
  cookingDayData?: CookingDayReportData | null;
  /** Per-location S/M/L breakdown (for sign up sheet and cooking day views) */
  byLocationData?: RSVPLocationBreakdownItem[];
  /** Menu info for single day header (date and title) */
  menuInfo?: { date: string; title: string } | null;
}

/**
 * ReportTable - Renders RSVP report data in different view formats
 *
 * Views:
 * - Summary: Date, Menu, S/M/L counts, Number of Families
 * - Sign up Sheet: Location, Member Name, Thaali #, Size (grouped by location)
 * - Cooking Day: Union-by-member table with one selection column per service day
 */
export function ReportTable({
  view,
  summaryData,
  detailsData,
  cookingDayData,
  byLocationData,
  menuInfo,
}: ReportTableProps) {
  switch (view) {
    case 'summary':
      return <SummaryTable data={summaryData} />;
    case 'signupSheet':
      return (
        <SignupSheetTable
          data={detailsData}
          byLocationData={byLocationData}
          menuInfo={menuInfo}
        />
      );
    case 'cookingDay':
      return <CookingDayTable data={cookingDayData} byLocationData={byLocationData} />;
    default:
      return null;
  }
}

// ============================================================================
// Summary View
// ============================================================================

interface SummaryTableProps {
  data: RSVPSummaryItem[];
}

function SummaryTable({ data }: SummaryTableProps) {
  if (data.length === 0) {
    return <EmptyState message="No RSVP data for this period" />;
  }

  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4 font-semibold text-sm">Date</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Menu</th>
              <th className="w-24 text-right py-3 px-4 font-semibold text-sm">S Count</th>
              <th className="w-24 text-right py-3 px-4 font-semibold text-sm">M Count</th>
              <th className="w-24 text-right py-3 px-4 font-semibold text-sm">L Count</th>
              <th className="text-right py-3 px-4 font-semibold text-sm"># of Families</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr
                key={`${item.date}-${item.menuId}`}
                className="border-b hover:bg-accent/50 transition-colors"
              >
                <td className="py-3 px-4 text-sm">
                  {formatDate(fromISODate(item.date), 'EEE, MMM d')}
                </td>
                <td className="py-3 px-4 text-sm font-medium">{item.menuTitle}</td>
                <td className="py-3 px-4 text-sm text-right">{item.smallCount}</td>
                <td className="py-3 px-4 text-sm text-right">{item.mediumCount}</td>
                <td className="py-3 px-4 text-sm text-right">{item.largeCount}</td>
                <td className="py-3 px-4 text-sm text-right">
                  <Badge variant="secondary">{item.totalRsvps}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {data.map((item) => (
          <Card key={`${item.date}-${item.menuId}`} className="p-4">
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{item.menuTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(fromISODate(item.date), 'EEEE, MMM d')}
                  </p>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {item.totalRsvps} families
                </Badge>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                {RSVP_SIZES.map((size) => (
                  <div key={size} className="flex items-center gap-1">
                    <span className="text-muted-foreground">{size}:</span>
                    <span className="font-medium">{getSummaryCount(item, size)}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

// ============================================================================
// Sign up Sheet View
// ============================================================================

interface SignupSheetTableProps {
  data: RSVPReportItem[];
  byLocationData?: RSVPLocationBreakdownItem[];
  menuInfo?: { date: string; title: string } | null;
}

function SignupSheetTable({ data, byLocationData, menuInfo }: SignupSheetTableProps) {
  const sortedData = useMemo(
    () => [...data].sort(compareLocationSorted),
    [data]
  );

  const firstOfLocationSet = useMemo(
    () => createFirstOfLocationSet(sortedData),
    [sortedData]
  );

  const locationGroups = useMemo(
    () => groupByLocation(sortedData),
    [sortedData]
  );

  if (data.length === 0) {
    return <EmptyState message="No RSVPs for this day" />;
  }

  return (
    <div className="space-y-4">
      {menuInfo && (
        <div className="bg-muted/50 rounded-lg p-4 border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <p className="text-lg font-semibold">{menuInfo.title}</p>
              <p className="text-sm text-muted-foreground">
                {formatDate(fromISODate(menuInfo.date), 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
            <Badge variant="outline" className="w-fit">
              {data.length} {data.length === 1 ? 'RSVP' : 'RSVPs'}
            </Badge>
          </div>
        </div>
      )}

      <LocationSizeBreakdownSection data={byLocationData} />

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4 font-semibold text-sm">Location</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Type</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Member Name</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">ITS ID</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Thaali #</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Phone</th>
              <th className="text-center py-3 px-4 font-semibold text-sm">Size</th>
              <th className="text-center py-3 px-4 font-semibold text-sm">Mithaas</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Optional Choices</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => {
              const isFirstOfLocation = firstOfLocationSet.has(index);
              const optOutSummary = (item.optionalChoices?.length ?? 0) > 0
                ? formatOptOutSummary(item.optionalChoices)
                : '';
              const hasOptOuts = item.optionalChoices?.some((c) => c.optedOut) ?? false;

              return (
                <tr
                  key={`${item.participantType}-${item.memberId}-${index}`}
                  className={cn(
                    'border-b hover:bg-accent/50 transition-colors',
                    isFirstOfLocation && index > 0 && 'border-t-2 border-t-primary/30'
                  )}
                >
                  <td
                    className={cn(
                      'py-3 px-4 text-sm font-medium',
                      isFirstOfLocation && 'font-semibold'
                    )}
                  >
                    <LocationColorBadge colorCode={item.pickupLocationColorCode}>
                      {isFirstOfLocation ? (
                        item.pickupLocationName || 'Unassigned'
                      ) : (
                        <span className="opacity-60">
                          {item.pickupLocationName || 'Unassigned'}
                        </span>
                      )}
                    </LocationColorBadge>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    <Badge variant={item.participantType === 'PUBLIC' ? 'secondary' : 'outline'}>
                      {item.participantType === 'PUBLIC' ? 'Public' : 'Member'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-sm">{item.memberName}</td>
                  <td className="py-3 px-4 text-sm">{item.memberId}</td>
                  <td className="py-3 px-4 text-sm">
                    <LocationColorBadge colorCode={item.pickupLocationColorCode}>
                      {item.thaaliNumber || 'N/A'}
                    </LocationColorBadge>
                  </td>
                  <td className="py-3 px-4 text-sm">{item.phone || 'N/A'}</td>
                  <td className="py-3 px-4 text-center">
                    <SelectionBadge size={item.size} />
                  </td>
                  <MithaasCell mithaas={item.mithaas} />
                  <td className="py-3 px-4 text-sm">
                    {optOutSummary && (
                      <span className={hasOptOuts ? 'text-amber-600' : 'text-muted-foreground'}>
                        {optOutSummary}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-4">
        {locationGroups.map((group) => (
          <div key={group.key} className="space-y-2">
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-md"
              style={getLocationBadgeStyles(group.colorCode)}
            >
              <span className="font-semibold text-sm">{group.name}</span>
              <Badge variant="outline" className="ml-auto text-xs">
                {group.items.length}
              </Badge>
            </div>

            <div className="space-y-2 pl-4">
              {group.items.map((item, idx) => (
                <Card key={`${item.participantType}-${item.memberId}-${idx}`} className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{item.memberName}</p>
                        <Badge variant={item.participantType === 'PUBLIC' ? 'secondary' : 'outline'} className="text-[10px]">
                          {item.participantType === 'PUBLIC' ? 'Public' : 'Member'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        ITS {item.memberId} · #{item.thaaliNumber || 'N/A'}
                      </p>
                      {item.phone && (
                        <p className="text-xs text-muted-foreground">{item.phone}</p>
                      )}
                      {item.optionalChoices?.some((c) => c.optedOut) && (
                        <p className="text-xs text-amber-600">
                          {formatOptOutSummary(item.optionalChoices)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <MithaasPill mithaas={item.mithaas} />
                      <SelectionBadge size={item.size} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Cooking Day View
// ============================================================================

interface CookingDayTableProps {
  data?: CookingDayReportData | null;
  byLocationData?: RSVPLocationBreakdownItem[];
}

function CookingDayTable({ data, byLocationData }: CookingDayTableProps) {
  const menus = data?.menus || [];
  const rows = data?.rows || [];

  const sortedRows = useMemo(
    () => [...rows].sort(compareLocationSorted),
    [rows]
  );

  const firstOfLocationSet = useMemo(
    () => createFirstOfLocationSet(sortedRows),
    [sortedRows]
  );

  const locationGroups = useMemo(
    () => groupByLocation(sortedRows),
    [sortedRows]
  );

  if (!data || menus.length === 0 || rows.length === 0) {
    return <EmptyState message="No RSVPs for this cooking day" />;
  }

  return (
    <div className="space-y-6">
      <div className="bg-muted/50 rounded-lg p-4 border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <p className="text-lg font-semibold">Cooking Day Report</p>
            <p className="text-sm text-muted-foreground">
              {formatDate(fromISODate(data.cookingDate), 'EEEE, MMMM d, yyyy')}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">
              {menus.length} {menus.length === 1 ? 'service day' : 'service days'}
            </Badge>
            <Badge variant="outline">
              {rows.length} {rows.length === 1 ? 'family' : 'families'}
            </Badge>
          </div>
        </div>
      </div>

      <LocationTotalsSection data={data} />
      <LocationSizeBreakdownSection
        data={byLocationData}
        getGroupTitle={(group) => getCookingDayGroupTitle(group, menus)}
      />
      <CookingDayMenuLegend menus={menus} />

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4 font-semibold text-sm">Location</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Type</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Member Name</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">ITS ID</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Thaali #</th>
              <th className="text-left py-3 px-4 font-semibold text-sm">Phone</th>
              <th className="text-center py-3 px-4 font-semibold text-sm">Mithaas</th>
              {menus.map((menu) => (
                <th
                  key={menu.menuId}
                  className="text-center py-3 px-4 font-semibold text-sm whitespace-nowrap"
                >
                  {formatDate(fromISODate(menu.serviceDate), 'EEE, MMM d')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => {
              const isFirstOfLocation = firstOfLocationSet.has(index);

              return (
                <tr
                  key={`${row.participantType}-${row.memberId}`}
                  className={cn(
                    'border-b hover:bg-accent/50 transition-colors',
                    isFirstOfLocation && index > 0 && 'border-t-2 border-t-primary/30'
                  )}
                >
                  <td
                    className={cn(
                      'py-3 px-4 text-sm font-medium',
                      isFirstOfLocation && 'font-semibold'
                    )}
                  >
                    <LocationColorBadge colorCode={row.pickupLocationColorCode}>
                      {isFirstOfLocation ? (
                        row.pickupLocationName || 'Unassigned'
                      ) : (
                        <span className="opacity-60">
                          {row.pickupLocationName || 'Unassigned'}
                        </span>
                      )}
                    </LocationColorBadge>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    <Badge variant={row.participantType === 'PUBLIC' ? 'secondary' : 'outline'}>
                      {row.participantType === 'PUBLIC' ? 'Public' : 'Member'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-sm">{row.memberName}</td>
                  <td className="py-3 px-4 text-sm">{row.memberId}</td>
                  <td className="py-3 px-4 text-sm">
                    <LocationColorBadge colorCode={row.pickupLocationColorCode}>
                      {row.thaaliNumber || 'N/A'}
                    </LocationColorBadge>
                  </td>
                  <td className="py-3 px-4 text-sm">{row.phone || 'N/A'}</td>
                  <MithaasCell mithaas={row.mithaas} />
                  {menus.map((menu) => {
                    const selection = getSelectionForMenu(row, menu.menuId);
                    const optOutText = getOptOutSummaryForMenu(row, menu);
                    return (
                      <td key={`${row.memberId}-${menu.menuId}`} className="py-3 px-4 text-center">
                        <SelectionBadge size={selection} />
                        {optOutText && (
                          <p className={cn(
                            'text-xs mt-0.5',
                            optOutText === OPT_OUT_ALL_INCLUDED ? 'text-muted-foreground' : 'text-amber-600'
                          )}>{optOutText}</p>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-4">
        {locationGroups.map((group) => (
          <div key={group.key} className="space-y-2">
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-md"
              style={getLocationBadgeStyles(group.colorCode)}
            >
              <span className="font-semibold text-sm">{group.name}</span>
              <Badge variant="outline" className="ml-auto text-xs">
                {group.items.length}
              </Badge>
            </div>

            <div className="space-y-2 pl-4">
              {group.items.map((row) => (
                <Card key={`${row.participantType}-${row.memberId}`} className="p-3 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{row.memberName}</p>
                        <Badge variant={row.participantType === 'PUBLIC' ? 'secondary' : 'outline'} className="text-[10px]">
                          {row.participantType === 'PUBLIC' ? 'Public' : 'Member'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        ITS {row.memberId} · #{row.thaaliNumber || 'N/A'}
                      </p>
                      {row.phone && (
                        <p className="text-xs text-muted-foreground">{row.phone}</p>
                      )}
                    </div>
                    <MithaasPill mithaas={row.mithaas} />
                  </div>

                  <div className="grid gap-2">
                    {menus.map((menu) => {
                      const optOutText = getOptOutSummaryForMenu(row, menu);
                      return (
                        <div
                          key={`${row.memberId}-${menu.menuId}`}
                          className="flex items-center justify-between gap-3"
                        >
                          <span className="text-xs text-muted-foreground">
                            {formatDate(fromISODate(menu.serviceDate), 'EEE, MMM d')}
                          </span>
                          <div className="text-right">
                            <SelectionBadge size={getSelectionForMenu(row, menu.menuId)} />
                            {optOutText && (
                              <p className={cn(
                                'text-xs',
                                optOutText === OPT_OUT_ALL_INCLUDED ? 'text-muted-foreground' : 'text-amber-600'
                              )}>{optOutText}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface LocationTotalsSectionProps {
  data: CookingDayReportData;
}

function LocationTotalsSection({ data }: LocationTotalsSectionProps) {
  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold">Total Count by Pickup Location</p>
        <p className="text-xs text-muted-foreground">
          Counts are based on displayed families in this combined report.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {data.locationTotals.map((total) => (
          <Card key={`location-total-${total.locationId ?? 'unassigned'}`} className="p-3">
            <div className="flex items-center justify-between gap-3">
              <LocationColorBadge colorCode={total.locationColorCode}>
                {total.locationName}
              </LocationColorBadge>
              <Badge variant="secondary">{total.totalCount}</Badge>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

interface LocationSizeBreakdownSectionProps {
  data?: RSVPLocationBreakdownItem[];
  /** Optional per-group heading (e.g. "Day A — Fri, Jul 31 (Menu Title)") */
  getGroupTitle?: (group: LocationSizeBreakdownGroup) => string | null;
}

/**
 * S/M/L counts per pickup location, one table per service day/menu,
 * each with a grand-total footer row.
 */
function LocationSizeBreakdownSection({
  data,
  getGroupTitle,
}: LocationSizeBreakdownSectionProps) {
  const groups = useMemo(() => groupLocationSizeBreakdown(data || []), [data]);

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold">Size Breakdown by Pickup Location</p>
        <p className="text-xs text-muted-foreground">
          Counts are individual RSVPs, so a family appears in each day it has an RSVP.
        </p>
      </div>
      <div className={cn('grid gap-4', groups.length > 1 && 'xl:grid-cols-2')}>
        {groups.map((group) => {
          // Fall back to a date/menu title whenever several tables render,
          // so they stay distinguishable even without a caller-provided label.
          const title = getGroupTitle
            ? getGroupTitle(group)
            : groups.length > 1
              ? `${formatDate(fromISODate(group.date), 'EEE, MMM d')} (${group.menuTitle})`
              : null;

          return (
            <div
              key={`${group.date}-${group.menuId}`}
              className="border rounded-lg overflow-hidden"
            >
              {title && (
                <div className="bg-muted/40 px-4 py-2 border-b">
                  <p className="text-sm font-semibold">{title}</p>
                </div>
              )}
              <div className="overflow-x-auto">
                <table
                  className="w-full"
                  aria-label={
                    title
                      ? `Size breakdown by pickup location — ${title}`
                      : 'Size breakdown by pickup location'
                  }
                >
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4 font-semibold text-sm">Location</th>
                      <th className="w-14 text-right py-2 px-4 font-semibold text-sm">S</th>
                      <th className="w-14 text-right py-2 px-4 font-semibold text-sm">M</th>
                      <th className="w-14 text-right py-2 px-4 font-semibold text-sm">L</th>
                      <th className="w-20 text-right py-2 px-4 font-semibold text-sm">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item) => (
                      <tr
                        key={`${item.locationId ?? 'unassigned'}-${item.locationName}`}
                        className="border-b hover:bg-accent/50 transition-colors"
                      >
                        <td className="py-2 px-4 text-sm">
                          <LocationColorBadge colorCode={item.locationColorCode}>
                            {item.locationName}
                          </LocationColorBadge>
                        </td>
                        <td className="py-2 px-4 text-sm text-right">{item.smallCount}</td>
                        <td className="py-2 px-4 text-sm text-right">{item.mediumCount}</td>
                        <td className="py-2 px-4 text-sm text-right">{item.largeCount}</td>
                        <td className="py-2 px-4 text-sm text-right font-medium">
                          {item.totalRsvps}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/40 font-semibold">
                      <td className="py-2 px-4 text-sm">Total</td>
                      <td className="py-2 px-4 text-sm text-right">{group.totals.smallCount}</td>
                      <td className="py-2 px-4 text-sm text-right">{group.totals.mediumCount}</td>
                      <td className="py-2 px-4 text-sm text-right">{group.totals.largeCount}</td>
                      <td className="py-2 px-4 text-sm text-right">{group.totals.totalRsvps}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Heading like "Day A — Fri, Jul 31 (Menu Title)" using the same day-letter
 * order as the cooking day service columns (menus sorted by service date).
 */
function getCookingDayGroupTitle(
  group: LocationSizeBreakdownGroup,
  menus: CookingDayMenuItem[]
): string {
  const menuIndex = menus.findIndex((menu) => menu.menuId === group.menuId);
  const dayLabel = menuIndex >= 0 ? `Day ${String.fromCharCode(65 + menuIndex)} — ` : '';
  const dateLabel = formatDate(fromISODate(group.date), 'EEE, MMM d');
  return `${dayLabel}${dateLabel} (${group.menuTitle})`;
}

interface CookingDayMenuLegendProps {
  menus: CookingDayMenuItem[];
}

function CookingDayMenuLegend({ menus }: CookingDayMenuLegendProps) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-muted/40 px-4 py-3 border-b">
        <p className="text-sm font-semibold">Service Day Menu Legend</p>
      </div>
      <div className="divide-y">
        {menus.map((menu) => (
          <div
            key={`legend-${menu.menuId}`}
            className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4"
          >
            <span className="text-sm font-medium whitespace-nowrap">
              {formatDate(fromISODate(menu.serviceDate), 'EEEE, MMM d')}
            </span>
            <span className="text-sm text-muted-foreground">{menu.menuTitle}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function getSummaryCount(item: RSVPSummaryItem, size: RSVPSizeCode): number {
  if (size === 'S') return item.smallCount;
  if (size === 'M') return item.mediumCount;
  return item.largeCount;
}

function SelectionBadge({ size }: { size: RSVPSizeCode | null }) {
  if (size === null) {
    return (
      <Badge
        variant="outline"
        className="bg-black text-white border-black hover:bg-black"
      >
        No Thaali
      </Badge>
    );
  }

  return <Badge variant={size === 'L' ? 'default' : 'secondary'}>{size}</Badge>;
}

function getSelectionForMenu(row: CookingDayReportRow, menuId: number): RSVPSizeCode | null {
  return row.selections.find((selection) => selection.menuId === menuId)?.size ?? null;
}

/** Build "No X, No Y" string for a member's opt-outs on a given cooking-day menu. */
function getOptOutSummaryForMenu(
  row: CookingDayReportRow,
  menu: CookingDayMenuItem
): string {
  const selection = row.selections.find((s) => s.menuId === menu.menuId);
  if (!selection || !selection.size || !menu.optionalRecipes?.length) return '';
  const optedOutIds = new Set(selection.optedOutMenuRecipeIds || []);
  return formatOptOutSummary(
    menu.optionalRecipes.map((r) => ({
      recipeName: r.recipeName,
      optedOut: optedOutIds.has(r.menuRecipeId),
    }))
  );
}

// ============================================================================
// Empty State
// ============================================================================

interface EmptyStateProps {
  message: string;
}

function EmptyState({ message }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
}

// ============================================================================
// Mithaas
// ============================================================================

function MithaasCell({ mithaas }: { mithaas: boolean | undefined }) {
  const noMithaas = mithaas === false;
  return (
    <td
      className={cn(
        'py-3 px-4 text-sm text-center font-medium',
        noMithaas && 'bg-yellow-100 text-yellow-900'
      )}
    >
      {noMithaas ? 'No' : 'Yes'}
    </td>
  );
}

function MithaasPill({ mithaas }: { mithaas: boolean | undefined }) {
  const noMithaas = mithaas === false;
  return (
    <span
      className={cn(
        'text-xs px-2 py-0.5 rounded-full border whitespace-nowrap',
        noMithaas
          ? 'bg-yellow-100 border-yellow-300 text-yellow-900 font-medium'
          : 'border-transparent text-muted-foreground'
      )}
    >
      Mithaas: {noMithaas ? 'No' : 'Yes'}
    </span>
  );
}
